import React from 'react';
import { createMemoStyles, getDisplayName, safeRest } from '@wds/utils';
import { CloseButton } from '@wds/internal-components';
import { Fade } from '@wds/motion';
/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

var useStyles = createMemoStyles(function (theme) {
  var type = theme.type,
      spacing = theme.spacing,
      palette = theme.palette,
      radius = theme.radius,
      shadow = theme.shadow;
  var chip = {
    height: {
      sm: 24,
      md: 32
    }
  };

  var borderColor = function (selected) {
    return selected ? palette.secondary.main : palette.border.main;
  };

  var interactionStyles = function (variant) {
    return {
      '&:hover': {
        backgroundColor: "rgba(0,0,0,0.2)",
        border: "solid 1px ".concat(palette.secondary.main),
        boxShadow: shadow.sky,
        cursor: variant === 'toggle' ? 'pointer' : 'inherit'
      },
      '&:active': {
        backgroundColor: "rgba(0,0,0,0.4)",
        boxShadow: 'none'
      }
    };
  };

  var chipStyles = {
    wrenchChip: function (_a) {
      var disabled = _a.disabled,
          selected = _a.selected,
          variant = _a.variant,
          size = _a.size,
          iconCheckEnclosed = _a.iconCheckEnclosed;
      return __assign({
        display: 'inline-flex',
        alignItems: 'center',
        position: 'relative',
        padding: [0, spacing[3], 0, iconCheckEnclosed ? spacing[2] : spacing[3]],
        height: chip.height[size],
        lineHeight: "".concat(chip.height[size], "px"),
        borderRadius: radius.medium,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        verticalAlign: 'middle',
        maxWidth: '100%',
        backgroundColor: variant === 'dismissible' ? palette.background.accent : palette.background.light,
        color: palette.text.dark,
        border: variant === 'toggle' ? "solid 1px ".concat(borderColor(selected)) : "solid 1px transparent",
        opacity: disabled && '0.5',
        cursor: disabled && 'default',
        boxSizing: 'border-box'
      }, !disabled && variant !== 'dismissible' && interactionStyles(variant));
    },
    wrenchChipIcon: function (_a) {
      var disabled = _a.disabled,
          selected = _a.selected;
      return {
        display: 'flex',
        height: spacing[4],
        width: spacing[4],
        fontSize: spacing[4],
        marginRight: spacing[2],
        '& > svg': {
          filter: !selected || disabled ? 'brightness(0) invert(0.5)' : null,
          opacity: !selected || disabled ? 0.5 : null
        }
      };
    },
    wrenchChipContent: function (_a) {
      var selected = _a.selected;
      return {
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontWeight: selected ? type.fontWeight.medium : type.fontWeight.regular,
        fontFamily: type.fontFamily.base,
        fontSize: type.fontSize.bodySm
      };
    }
  };
  return chipStyles;
});

var Chip = function (_a) {
  var children = _a.children,
      _b = _a.size,
      size = _b === void 0 ? 'md' : _b,
      _c = _a.disabled,
      disabled = _c === void 0 ? false : _c,
      _d = _a.icon,
      icon = _d === void 0 ? undefined : _d,
      _e = _a.variant,
      variant = _e === void 0 ? 'dismissible' : _e,
      _f = _a.selected,
      selected = _f === void 0 ? true : _f,
      _g = _a.closeLabel,
      closeLabel = _g === void 0 ? 'Dismiss badge' : _g,
      _h = _a.onClick,
      onClick = _h === void 0 ? undefined : _h,
      rest = __rest(_a, ["children", "size", "disabled", "icon", "variant", "selected", "closeLabel", "onClick"]);

  var iconCheckEnclosed = getDisplayName(icon) === 'WithIcon(IconCheckEnclosed)' && true;

  var _j = useStyles({
    disabled: disabled,
    size: size,
    variant: variant,
    selected: selected,
    iconCheckEnclosed: iconCheckEnclosed
  }),
      wrenchChip = _j.wrenchChip,
      wrenchChipIcon = _j.wrenchChipIcon,
      wrenchChipContent = _j.wrenchChipContent;

  var handleClick = function () {
    if (onClick) {
      onClick();
    }
  };

  var renderChipContent = function () {
    return React.createElement(React.Fragment, null, icon && React.createElement("span", {
      className: wrenchChipIcon
    }, icon), React.createElement("span", {
      className: wrenchChipContent
    }, children), variant === 'dismissible' && React.createElement(CloseButton, {
      label: closeLabel,
      variant: 'dark',
      onClick: handleClick,
      component: "badge",
      disabled: disabled
    }));
  };

  var renderChipVariant = function () {
    if (variant === 'dismissible') {
      return React.createElement(Fade, {
        in: selected
      }, React.createElement("div", null, React.createElement("div", __assign({
        className: wrenchChip
      }, safeRest(rest)), renderChipContent())));
    }

    return React.createElement("button", __assign({
      type: "button",
      disabled: disabled,
      onClick: handleClick,
      className: wrenchChip
    }, safeRest(rest)), renderChipContent());
  };

  return React.createElement(React.Fragment, null, renderChipVariant());
};

var CHIP_SIZES = ['sm', 'md'];
var CHIP_VARIANTS = ['dismissible', 'toggle'];
export { CHIP_SIZES, CHIP_VARIANTS, Chip };